import React, { useState } from "react";
import { first } from "lodash";
import Various from "@danskernesdigitalebibliotek/dpl-design-system/build/icons/collection/Various.svg";
import { useQueryClient } from "@tanstack/react-query";
import { useModalButtonHandler } from "../../core/utils/modal";
import {
  convertPostIdsToFaustIds,
  getAllPids,
  getMaterialType,
  getWorkPid,
  materialIsFiction
} from "../../core/utils/helpers/general";
import { useText } from "../../core/utils/text";
import { Button } from "../Buttons/Button";
import { Cover } from "../cover/cover";
import ReservationFormListItem from "./ReservationFormListItem";
import {
  AgencyBranch,
  HoldingsForBibliographicalRecordLogisticsV1,
  ReservationResponseV2
} from "../../core/fbs/model";
import UserListItems from "./UserListItems";
import ReservationSucces from "./ReservationSucces";
import ReservationError from "./ReservationError";
import {
  getManifestationTitle,
  getTotalHoldings,
  getTotalReservations,
  reservationModalId,
  editionSwitchModalId,
  useGetHoldings
} from "../../apps/material/helper";
import {
  getGetHoldingsLogisticsV1QueryKey,
  useAddReservationsV2
} from "../../core/fbs/fbs";
import {
  AuthenticatedPatron,
  Manifestation,
  Work
} from "../../core/utils/types/entities";
import {
  canSubmitOpenOrderReservation,
  getPreferredBranch,
  constructReservationData,
  getAuthorLine,
  getManifestationsToReserve,
  getInstantLoanBranchHoldings,
  getInstantLoanBranchHoldingsAboveThreshold,
  removePrefixFromBranchId,
  translateOpenOrderStatus
} from "./helper";
import UseReservableManifestations from "../../core/utils/UseReservableManifestations";
import { PeriodicalEdition } from "../material/periodical/helper";
import { useConfig } from "../../core/utils/config";
import { useEventStatistics } from "../../core/statistics/useStatistics";
import StockAndReservationInfo from "../material/StockAndReservationInfo";
import MaterialAvailabilityTextParagraph from "../material/MaterialAvailabilityText/generic/MaterialAvailabilityTextParagraph";
import { statistics } from "../../core/statistics/statistics";
import useAlternativeAvailableManifestation from "./useAlternativeAvailableManifestation";
import PromoBar from "../promo-bar/PromoBar";
import InstantLoan from "../instant-loan/InstantLoan";
import { excludeBlacklistedBranches } from "../../core/utils/branches";
import { InstantLoanConfigType } from "../../core/utils/types/instant-loan";
import {
  OpenOrderMutation,
  useOpenOrderMutation,
  useWorkRecommendationsQuery
} from "../../core/dbc-gateway/generated/graphql";
import ModalMessage from "../message/modal-message/ModalMessage";
import configuration, { getConf } from "../../core/configuration";
import useReservableFromAnotherLibrary from "../../core/utils/useReservableFromAnotherLibrary";
import { usePatronData } from "../../core/utils/helpers/usePatronData";
import { Periods } from "./types";
import { RequestStatus } from "../../core/utils/types/request";
import {
  getFutureDateString,
  getFutureDateStringISO
} from "../../core/utils/helpers/date";
import MaterialGrid from "../material-grid/MaterialGrid";
import { WorkId } from "../../core/utils/types/ids";

type ReservationModalProps = {
  selectedManifestations: Manifestation[];
  selectedPeriodical: PeriodicalEdition | null;
  work: Work;
};

export const ReservationModalBody = ({
  selectedManifestations,
  selectedPeriodical,
  work
}: ReservationModalProps) => {
  const t = useText();
  const config = useConfig();
  const { open } = useModalButtonHandler();
  const { defaultInterestDaysForOpenOrder } = getConf(
    "reservation",
    configuration
  );
  const {
    matchStrings: instantLoanMatchStrings,
    threshold: instantLoanThreshold,
    enabled: instantLoanEnabled
  } = config<InstantLoanConfigType>("instantLoanConfig", {
    transformer: "jsonParse"
  });

  const branches = config<AgencyBranch[]>("branchesConfig", {
    transformer: "jsonParse"
  });
  const blacklistBranchesInstantLoan = config(
    "blacklistedInstantLoanBranchesConfig",
    {
      transformer: "stringToArray"
    }
  );
  const blacklistPickupBranches = config("blacklistedPickupBranchesConfig", {
    transformer: "stringToArray"
  });
  const whitelistBranches = excludeBlacklistedBranches(
    branches,
    blacklistBranchesInstantLoan.concat(blacklistPickupBranches)
  );
  const mainManifestationType = getMaterialType(selectedManifestations);
  const { reservableManifestations } = UseReservableManifestations({
    manifestations: selectedManifestations,
    type: mainManifestationType
  });
  const queryClient = useQueryClient();
  const [reservationResponse, setReservationResponse] =
    useState<ReservationResponseV2 | null>(null);
  const [openOrderResponse, setOpenOrderResponse] =
    useState<OpenOrderMutation | null>(null);
  const [selectedBranch, setSelectedBranch] = useState<string | null>(null);
  const [selectedInterest, setSelectedInterest] = useState<number | null>(null);
  const allPids = getAllPids(selectedManifestations);
  const faustIds = convertPostIdsToFaustIds(allPids);
  const { mutate: mutateAddReservations } = useAddReservationsV2();
  const { mutate: mutateOpenOrder } = useOpenOrderMutation();
  const userResponse = usePatronData();
  const holdingsResponse = useGetHoldings({
    faustIds,
    config,
    blacklist: "availability"
  });
  const { track } = useEventStatistics();
  const { otherManifestationPreferred } = useAlternativeAvailableManifestation(
    work,
    allPids
  );
  const manifestationsToReserve = getManifestationsToReserve(
    reservableManifestations ?? [],
    !!selectedPeriodical
  );
  const [reservationStatus, setReservationStatus] =
    useState<RequestStatus>("idle");
  const {
    reservablePidsFromAnotherLibrary,
    materialIsReservableFromAnotherLibrary
  } = useReservableFromAnotherLibrary(selectedManifestations);

  // If we don't have all data for displaying the view render nothing.
  if (!userResponse.data || !holdingsResponse.data) {
    return null;
  }
  const { data: userData } = userResponse as { data: AuthenticatedPatron };
  const { data: holdingsData } = holdingsResponse as {
    data: HoldingsForBibliographicalRecordLogisticsV1[];
  };
  const holdings = getTotalHoldings(holdingsData);
  const reservations = getTotalReservations(holdingsData);
  const { patron } = userData;
  const authorLine = getAuthorLine(first(selectedManifestations), t);
  const interestPeriods = config<Periods>("interestPeriodsConfig", {
    transformer: "jsonParse"
  });
  const interestPeriod =
    selectedInterest || interestPeriods.defaultInterestPeriod.value;
  const expiryDate = getFutureDateString(interestPeriod);
  const materialType = getMaterialType(selectedManifestations);

  const userHasEmail = Boolean(patron?.emailAddress);

  const canSubmitFbs =
    manifestationsToReserve?.length && !materialIsReservableFromAnotherLibrary;
  const canSubmitOpenOrder = canSubmitOpenOrderReservation({
    materialIsReservableFromAnotherLibrary,
    patron
  });

  const saveReservation = () => {
    if (canSubmitFbs) {
      setReservationStatus("pending");
      // Save reservation to FBS.
      mutateAddReservations(
        {
          data: constructReservationData({
            manifestations: manifestationsToReserve,
            selectedBranch,
            expiryDate,
            periodical: selectedPeriodical
          })
        },
        {
          onSuccess: (res) => {
            setReservationStatus("success");
            // Track only if the reservation has been successfully saved.
            track("click", {
              id: statistics.reservation.id,
              name: statistics.reservation.name,
              trackedData: `${work.workId} ${materialType}`
            });
            // This state is used to show the success or error modal.
            setReservationResponse(res);
            // Because after a successful reservation the holdings (reservations) are updated.
            queryClient.invalidateQueries({
              queryKey: getGetHoldingsLogisticsV1QueryKey({
                recordid: faustIds
              })
            });
          },
          onError: () => {
            setReservationStatus("error");
          }
        }
      );
    } else if (canSubmitOpenOrder && patron) {
      setReservationStatus("pending");
      const { patronId, name, emailAddress, preferredPickupBranch } = patron;
      // Save reservation to open order.
      mutateOpenOrder(
        {
          input: {
            pids: reservablePidsFromAnotherLibrary,
            pickUpBranch: selectedBranch
              ? removePrefixFromBranchId(selectedBranch)
              : removePrefixFromBranchId(preferredPickupBranch),
            expires: getFutureDateStringISO(
              Number(selectedInterest ?? defaultInterestDaysForOpenOrder)
            ),
            userParameters: {
              userId: patronId.toString(),
              userName: name,
              userMail: emailAddress
            }
          }
        },
        {
          onSuccess: (res) => {
            setReservationStatus("success");
            setOpenOrderResponse(res);
          },
          onError: () => {
            setReservationStatus("error");
          }
        }
      );
    }
  };

  const reservationSuccess = reservationResponse?.success || false;
  const reservationResults = reservationResponse?.reservationResults;
  const reservationDetails =
    reservationResponse?.reservationResults[0]?.reservationDetails;
  const manifestation =
    manifestationsToReserve?.[0] || selectedManifestations[0];
  const editionText =
    !materialIsFiction(work) || manifestationsToReserve?.length === 1
      ? manifestation.edition?.summary
      : t("firstAvailableEditionText");

  const instantLoanBranchHoldings = getInstantLoanBranchHoldings(
    holdingsData[0].holdings,
    whitelistBranches,
    instantLoanMatchStrings ?? []
  );

  const instantLoanBranchHoldingsAboveThreshold =
    getInstantLoanBranchHoldingsAboveThreshold(
      instantLoanBranchHoldings,
      instantLoanThreshold
    );

  // Disable submit based on the exact conditions used in saveReservation
  const isSubmitDisabled =
    reservationStatus === "pending" || !(canSubmitFbs || canSubmitOpenOrder);

  const handleEditionSwitchClick = () => {
    open(editionSwitchModalId());
  };

  return (
    <>
      {!reservationResults && !openOrderResponse && (
        <section className="reservation-modal">
          <header className="reservation-modal-header">
            <Cover ids={[manifestation.pid]} size="medium" animate />
            <div className="reservation-modal-description">
              <div className="reservation-modal-tag">{materialType}</div>
              <h2 className="text-header-h2 mt-22 mb-8">
                {getManifestationTitle(manifestation)}
                {selectedPeriodical && ` ${selectedPeriodical.displayText}`}
              </h2>
              {authorLine && (
                <p className="text-body-medium-regular">{authorLine}</p>
              )}
            </div>
          </header>
          <div>
            <div className="reservation-modal-submit">
              <MaterialAvailabilityTextParagraph>
                {materialIsReservableFromAnotherLibrary ? (
                  userHasEmail ? (
                    t("reservableFromAnotherLibraryExtraInfoText")
                  ) : (
                    t("reservableFromAnotherLibraryMissingEmailText")
                  )
                ) : (
                  <StockAndReservationInfo
                    stockCount={holdings}
                    reservationCount={reservations}
                  />
                )}
              </MaterialAvailabilityTextParagraph>
              <Button
                dataCy="reservation-modal-submit-button"
                label={t("approveReservationText")}
                buttonType="none"
                variant="filled"
                disabled={isSubmitDisabled}
                collapsible={false}
                size="small"
                onClick={saveReservation}
              />
            </div>
            <div className="reservation-modal-list">
              {instantLoanEnabled &&
                instantLoanBranchHoldingsAboveThreshold.length > 0 && (
                  <InstantLoan
                    manifestation={manifestation}
                    instantLoanBranchHoldings={
                      instantLoanBranchHoldingsAboveThreshold
                    }
                  />
                )}
              <ReservationFormListItem
                icon={Various}
                title={t("editionText")}
                text={selectedPeriodical?.displayText || editionText || ""}
                isPossibleToChangeReservationDetails
                changeHandler={handleEditionSwitchClick}
                buttonAriaLabel={t("editionSwitchButtonChangeText")}
              />
              {!materialIsFiction(work) && otherManifestationPreferred && (
                <PromoBar
                  classNames="px-35"
                  sticky
                  type="info"
                  text={t("materialIsAvailableInAnotherEditionText", {
                    placeholders: {
                      "@title": getManifestationTitle(
                        otherManifestationPreferred
                      ),
                      "@authorAndYear":
                        getAuthorLine(otherManifestationPreferred, t) ?? "",
                      "@reservations": otherManifestationPreferred.reservations
                    }
                  })}
                />
              )}
              {patron && (
                <UserListItems
                  whitelistBranches={whitelistBranches}
                  patron={patron}
                  branches={branches}
                  selectedBranch={selectedBranch}
                  selectBranchHandler={setSelectedBranch}
                  selectedInterest={
                    materialIsReservableFromAnotherLibrary &&
                    selectedInterest === null
                      ? Number(defaultInterestDaysForOpenOrder)
                      : selectedInterest
                  }
                  setSelectedInterest={setSelectedInterest}
                  reservationStatus={reservationStatus}
                />
              )}
            </div>
          </div>
        </section>
      )}
      {openOrderResponse?.submitOrder?.status && (
        <ModalMessage
          title={t("openOrderResponseTitleText")}
          subTitle={manifestation.titles.main[0]}
          ctaButton={{
            text: t("okButtonText"),
            modalId: reservationModalId(faustIds),
            dataCy: "reservation-success-close-button"
          }}
        >
          {openOrderResponse.submitOrder.status && (
            <p
              data-cy="open-oprder-response-status-text"
              className="text-body-medium-regular pt-24"
            >
              {translateOpenOrderStatus(
                openOrderResponse.submitOrder?.status,
                t
              )}
            </p>
          )}
        </ModalMessage>
      )}
      {reservationSuccess && reservationDetails && (
        <ReservationSucces
          title={getManifestationTitle(manifestation)}
          preferredPickupBranch={getPreferredBranch(
            reservationDetails.pickupBranch,
            branches
          )}
          holdings={holdings}
          numberInQueue={reservationDetails.numberInQueue}
          recommendations={<Recommendations work={work} />}
        />
      )}
      {!reservationSuccess && reservationResults && (
        <ReservationError
          reservationResults={reservationResults}
          setReservationResponse={setReservationResponse}
        />
      )}
    </>
  );
};

const Recommendations = (props: { work: Work }) => {
  const t = useText();
  const pid = getWorkPid(props.work);

  const { data: recommendationData } = useWorkRecommendationsQuery(
    {
      pid,
      limit: 4
    },
    { enabled: !!pid }
  );

  const recommendations = recommendationData?.recommend.result ?? [];

  const workIds = recommendations.map((recommendation) => {
    return recommendation.work.workId;
  });

  const materialProps = workIds.map((workId) => ({
    wid: workId as WorkId
  }));

  return (
    <div className="reservation-success__recommendations">
      <h3 className="reservation-success__recommendations-title">
        {t("reservationRecommendationsTitleText")}
      </h3>
      <MaterialGrid initialMaximumDisplay={4} materials={materialProps} />
    </div>
  );
};
